import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const auth = (token, version) => ({
  headers: {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(version === undefined || version === null ? {} : { "If-Match": version }),
  },
});

export const createOrder = ({ tableId, guestCount, orderType }, token) =>
  axios
    .post(
      `${API}/orders`,
      { table_id: tableId ?? null, guest_count: guestCount, order_type: orderType },
      auth(token)
    )
    .then(r => r.data);

export const startOrder = (payload, token) =>
  axios
    .post(`${API}/orders/start`, payload, auth(token))
    .then(r => r.data);

// Optimistic locking is not optional: without If-Match a second captain's edit
// silently overwrites the first.
export const addItems = (orderId, lines, version, token) =>
  axios.post(`${API}/orders/${orderId}/items`, lines, auth(token, version)).then(r => r.data);

// The preview as the PRINTER lays it out — the server decodes the actual
// ESC/POS byte stream, so screen and paper cannot drift (bible §12).
export const previewBillRender = (orderId, token) =>
  axios
    .post(`${API}/orders/${orderId}/bill/preview-render`, {}, auth(token))
    .then(r => r.data);

/** The table's Check Items slip, printed at its bill printer, not the kitchen (2026-10-24). */
export const printCheckItems = (orderId, token) =>
  axios.post(`${API}/orders/${orderId}/check-items`, {}, auth(token)).then(r => r.data);

/** The Check Items slip as text, decoded from the bytes the printer would get. Prints nothing. */
export const previewCheckItems = (orderId, token) =>
  axios.post(`${API}/orders/${orderId}/check-items/preview-render`, {}, auth(token)).then(r => r.data);

// A second printed copy of an already-billed order (marked Duplicate).
export const printDuplicateBill = (orderId, token) =>
  axios
    .post(`${API}/orders/${orderId}/bill/duplicate`, {}, auth(token))
    .then(r => r.data);

// Body is locked to these keys by docs/frontend-billing-contracts.md §1 —
// discount_reason must never appear in a preview body. `service_charge` (the
// cashier's choice, 2026-11-01) is sent only when the cashier made one.
export const previewBill = (orderId, { discount, discountPct, redeemPoints, serviceCharge }, token) =>
  axios
    .post(
      `${API}/orders/${orderId}/bill/preview`,
      {
        discount: discount ?? 0,
        discount_pct: discountPct ?? null,
        redeem_points: redeemPoints ?? 0,
        ...(typeof serviceCharge === "boolean" ? { service_charge: serviceCharge } : {}),
      },
      auth(token)
    )
    .then(r => r.data);

// Settlement is two calls: generate the bill (which prints and freezes it), then
// close it with the tender. Both take If-Match. `printBill: false` freezes it
// without printing — the billing PC's "Settle without printing"; the server
// refuses it from a captain.
export const generateBill = (orderId, { discount, discountPct, discountReason, redeemPoints, printBill, serviceCharge }, version, token) =>
  axios
    .post(
      `${API}/orders/${orderId}/bill`,
      {
        discount: discount ?? 0,
        discount_pct: discountPct ?? null,
        discount_reason: discountReason ?? null,
        redeem_points: redeemPoints ?? 0,
        ...(printBill === false ? { print_bill: false } : {}),
        ...(typeof serviceCharge === "boolean" ? { service_charge: serviceCharge } : {}),
      },
      auth(token, version)
    )
    .then(r => r.data);

/**
 * Close the bill. Pass `payments` for a split tender; the server rejects a split
 * whose lines do not sum to the grand total. Omit it for a single payment method.
 */
export const closeOrderOnServer = (orderId, paymentMethod, version, token, payments = null) =>
  axios
    .post(
      `${API}/orders/${orderId}/close${paymentMethod ? `?payment_method=${encodeURIComponent(paymentMethod)}` : ""}`,
      payments && payments.length ? { payments } : null,
      auth(token, version)
    )
    .then(r => r.data);

export const fireKot = (orderId, version, token) =>
  axios.post(`${API}/orders/${orderId}/kot`, {}, auth(token, version)).then(r => r.data);

export const listActiveOrders = token =>
  axios.get(`${API}/orders/active`, auth(token)).then(r => r.data);

/** Send whatever the server holds unsent on this order to the kitchen, at its
 *  current version (components/captain/SendWaitingButton). */
export const sendWaitingToKitchen = async (orderId, token) => {
  const order = await getOrder(orderId, token);
  return fireKot(orderId, order.version, token);
};

export const getOrder = (orderId, token) =>
  axios.get(`${API}/orders/${orderId}`, auth(token)).then(r => r.data);

// A line's special note, before the kitchen has the line. Blank clears it; a
// line already on a KOT is refused (409) — the kitchen holds that ticket.
export const setItemNote = (orderId, itemId, notes, version, token) =>
  axios
    .patch(`${API}/orders/${orderId}/items/${itemId}`, { notes }, auth(token, version))
    .then(r => r.data);

export const getOrderItems = (orderId, token) =>
  axios.get(`${API}/orders/${orderId}/items`, auth(token)).then(r => r.data);

/** Who sent each KOT, printed, settled, moved or cancelled the order (audit-derived). */
export const getOrderActivity = (orderId, token) =>
  axios.get(`${API}/orders/${orderId}/activity`, auth(token)).then(r => r.data);

export const updateOrder = (orderId, patch, version, token) =>
  axios.patch(`${API}/orders/${orderId}`, patch, auth(token, version)).then(r => r.data);

/**
 * Void one or more lines on an order.
 *
 * A line already fired to the kitchen cannot simply be deleted — it is being cooked,
 * and the station needs a VOID slip. This is the only correct way to remove an item
 * after a KOT, and the reason code feeds the void report.
 */
export const cancelItems = (orderId, itemIds, reasonCode, version, token) =>
  axios
    .post(
      `${API}/orders/${orderId}/cancel-items${reasonCode ? `?reason_code=${reasonCode}` : ""}`,
      itemIds,
      auth(token, version)
    )
    .then(r => r.data);

/**
 * Void `quantity` of ONE line — two of three beers — and keep the rest running.
 * The whole quantity cancels that line only; less splits it server-side so the
 * VOID slip prints the voided count. Never touches the other lines or the table.
 */
export const voidQuantity = (orderId, itemId, quantity, reasonCode, version, token) =>
  axios
    .post(
      `${API}/orders/${orderId}/void-quantity`,
      { item_id: itemId, quantity, ...(reasonCode ? { reason_code: reasonCode } : {}) },
      auth(token, version)
    )
    .then(r => r.data);

/**
 * Void the WHOLE order: every live line goes and the table comes back.
 *
 * This is not cancel-items over every row. Voiding the last line one at a time
 * leaves the order live, and `idx_one_active_order_per_table` then holds the table
 * for the rest of service — the endpoint that actually releases it is this one.
 *
 * A reason is mandatory: a coded `reasonCode`, free `reason` text, or both. Once a
 * KOT has been fired the FSM makes the cancel admin-only, and `authorizedBy` — the
 * user_id from `authorizeAction` — is how a captain's terminal carries a manager's
 * approval to it. The server re-verifies that claim; it is never taken on trust.
 */
export const cancelOrder = (
  orderId,
  { reasonCode = null, reason = null, authorizedBy = null } = {},
  version,
  token
) =>
  axios
    .post(
      `${API}/orders/${orderId}/cancel`,
      {
        reason_code: reasonCode ?? null,
        reason: reason ?? null,
        authorized_by: authorizedBy ?? null,
      },
      auth(token, version)
    )
    .then(r => r.data);

/** Void reasons an operator can pick from; `applies_to` narrows to item-level ones. */
export const listCancellationReasons = token =>
  axios
    .get(`${API}/cancellation-reasons`, { ...auth(token), params: { active_only: true } })
    .then(r => r.data);

/** Filtered board. `updatedAfter` is the incremental-sync cursor after a reconnect. */
export const listOrders = ({ status, tableId, updatedAfter } = {}, token) =>
  axios
    .get(`${API}/orders`, {
      ...auth(token),
      params: {
        ...(status ? { status: Array.isArray(status) ? status.join(",") : status } : {}),
        ...(tableId ? { table_id: tableId } : {}),
        ...(updatedAfter ? { updated_after: updatedAfter } : {}),
      },
    })
    .then(r => r.data);

/** Move an order to another table. The target must be free or the server 409s. */
export const transferOrder = (orderId, targetTableId, version, token) =>
  axios
    .post(`${API}/orders/${orderId}/transfer`, { target_table_id: targetTableId }, auth(token, version))
    .then(r => r.data);

/**
 * Fold `sourceOrderId` into `orderId`. The source is cancelled and its table freed.
 *
 * Both versions travel: `If-Match` guards the target, `source_version` the source,
 * whose lines move and which then gets cancelled. Only one version fits in a header.
 */
export const mergeOrders = (orderId, sourceOrderId, version, token, sourceVersion = null) =>
  axios
    .post(
      `${API}/orders/${orderId}/merge`,
      { source_order_id: sourceOrderId, source_version: sourceVersion },
      auth(token, version)
    )
    .then(r => r.data);

/**
 * Move selected lines onto a sibling order so one seating can pay two bills.
 *
 * This is what "split the bill" means at a table — each guest pays for what they
 * ate. Splitting the *total* into N equal shares is a payment concern and goes
 * through the tender array on close, not through here.
 */
export const splitOrder = (orderId, itemIds, version, token) =>
  axios
    .post(`${API}/orders/${orderId}/split`, { item_ids: itemIds }, auth(token, version))
    .then(r => r.data);

export const transferItems = (orderId, itemIds, targetTableId, version, token) =>
  axios
    .post(`${API}/orders/${orderId}/transfer_items`, { item_ids: itemIds, target_table_id: targetTableId }, auth(token, version))
    .then(r => r.data);

/** Active captains the billing PC may name on a new table (2026-10-24). */
export const listOrderCaptains = token =>
  axios.get(`${API}/orders/captains`, auth(token)).then(r => r.data);

/** Attach a customer to the order, which is what makes tiers and loyalty apply.
 *  The server reads customer_id from the query string; a JSON body was a 422. */
export const linkOrderCustomer = (orderId, customerId, token) =>
  axios
    .patch(`${API}/orders/${orderId}/customer`, null, { ...auth(token), params: { customer_id: customerId } })
    .then(r => r.data);

/**
 * A manager approving one action at somebody else's terminal.
 *
 * Issues no token and starts no session: it answers "does this employee hold this
 * privilege" and returns their identity. The user_id comes back here and is
 * relayed to the endpoint that acts on it, which re-verifies server-side — the
 * client's word that a manager approved is never enough.
 */
export const authorizeAction = (employeeId, pin, action, token) =>
  axios
    .post(
      `${API}/auth/authorize`,
      { employee_id: employeeId, pin, action },
      auth(token)
    )
    .then(r => r.data);

/** Comp the whole bill ('bill') or make it no-charge ('nc'). */
export const markComplimentary = (orderId, { scope, reason, authorizedBy }, version, token) =>
  axios
    .post(
      `${API}/orders/${orderId}/complimentary`,
      { scope, reason, authorized_by: authorizedBy },
      auth(token, version)
    )
    .then(r => r.data);


export const releaseTable = (tableId, token) =>
  axios.post(`${API}/tables/${tableId}/release`, {}, auth(token)).then(r => r.data);
